// Tab "Recuperi Pagamenti" dei Compensi: chi deve ancora pagare mesi passati e,
// da qui, la registrazione del pagamento.
//
// Gli arretrati sono quelli del registro pagamenti (GET /compensation/arretrati,
// calcolati da backend/stato_pagamenti.py come l'avviso in scheda cliente): un
// mese compare solo quando è finito e resta finché non è registrato, anche se i
// mesi dopo sono pagati. Registrare qui è la stessa scrittura della scheda
// cliente (routes/pagamenti_cliente.py): se il mese era chiuso dallo snapshot
// come non pagato, il backend crea da solo il recupero dei compensi nel payout
// del mese corrente, che compare nella colonna a fianco.
//
// I clienti che per un mese non sono seguiti dal registro (senza servizi, o mesi
// precedenti al registro) restano sulla vista per snapshot di prima, in fondo,
// con il recupero manuale.
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronDown, CircleDollarSign, ExternalLink, Loader2,
  RefreshCw, Search, Trash2, Undo2, Wallet,
} from "lucide-react";
import { Input } from "./ui/input";
import { formatCurrency } from "../utils/formatters";
import { puoRegistrarePagamenti } from "./PagamentiCliente";
import { MesiArretrati, avvisaRegistrato } from "./PagamentiArretrati";
import {
  SECONDARY_BTN, GHOST_BTN, FIELD_CLS,
  SectionCard, StatBlock, Badge, EmptyState, InfoHint, InfoTooltip, UserAvatar, MonthPicker,
} from "./designSystem";

const euro = (v) => formatCurrency(v, { decimals: Number(v) % 1 ? 2 : 0 });
const maiuscola = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const nomeMese = (ym) => new Date(`${ym}-01T12:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

function ClienteArretrato({ cliente, canRegister, onRegistrato, apertoIniziale }) {
  const navigate = useNavigate();
  const [aperto, setAperto] = useState(apertoIniziale);
  const n = cliente.mesi.length;
  return (
    <div className="rounded-[14px] border border-[#e9e9f1] bg-white overflow-hidden" data-testid={`arretrato-${cliente.client_id}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setAperto((a) => !a)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAperto((a) => !a); } }}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#fafafc] transition-colors cursor-pointer"
      >
        <UserAvatar name={cliente.name} size={30} shape="rounded" />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-[#15161c] truncate">{cliente.name}</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {cliente.mesi.map((m) => (
              <span key={m.mese} className="h-[20px] px-2 rounded-full bg-[#fdecea] text-[#c0392b] text-[11px] font-semibold inline-flex items-center">
                {maiuscola(m.nome_mese)}
              </span>
            ))}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[15px] font-bold scheda-font-mono text-[#c0392b]">
            {euro(cliente.totale)}{cliente.importo_parziale && <span className="text-[#9095a1] text-[12px]"> + %</span>}
          </div>
          <div className="text-[11.5px] text-[#9095a1]">{n === 1 ? "1 mese" : `${n} mesi`}</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-[#9095a1] shrink-0 transition-transform ${aperto ? "rotate-180" : ""}`} />
      </div>
      {aperto && (
        <div className="border-t border-[#f0f0f6]">
          <MesiArretrati mesi={cliente.mesi} clientId={cliente.client_id} canRegister={canRegister} onRegistrato={onRegistrato} />
          <div className="px-4 py-2 flex justify-end bg-[#fbfbfd]">
            <button className={GHOST_BTN} onClick={() => navigate(`/scheda-cliente/${cliente.client_id}`)}>
              Apri scheda cliente <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Vista per snapshot di prima, per i clienti che quel mese il registro non segue.
function SenzaRegistro({ api, meseCorrente, onRecupero }) {
  const [aperto, setAperto] = useState(false);
  const [mese, setMese] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.call(`/compensation/unpaid-clients?for_month=${mese}&fuori_registro=true`);
      if (r.ok) setClienti((await r.json()).clients || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (aperto) carica();
  }, [aperto, mese]); // eslint-disable-line react-hooks/exhaustive-deps

  const recupera = async (c) => {
    if (!window.confirm(`Registrare il recupero di "${c.name}" per ${nomeMese(mese)}?\n\nIl compenso di DT/DC/Sales verrà aggiunto al payout di ${nomeMese(meseCorrente)}.`)) return;
    setSaving(c.client_id);
    try {
      const r = await api.call("/compensation/payment-recoveries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: c.client_id, for_year_month: mese, recovered_in_year_month: meseCorrente }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Recupero registrato: +${euro(d.recovery.total_delta)}`);
        carica();
        onRecupero();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore");
      }
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="bg-white border border-[#e9e9f1] rounded-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.03)]" data-testid="recuperi-senza-registro">
      <button
        type="button"
        onClick={() => setAperto((a) => !a)}
        className="w-full flex items-center gap-2.5 px-5 py-4 text-left"
      >
        <span className="text-[14px] font-bold text-[#15161c]">Clienti fuori dal registro pagamenti</span>
        <InfoHint
          title="Recupero manuale"
          description="Clienti senza servizi, o mesi precedenti al registro pagamenti: il loro stato del mese era l'interruttore manuale Pagato. Qui si legge dallo snapshot di fine mese e il recupero dei compensi si registra a mano."
        />
        <span className="flex-1" />
        <ChevronDown className={`w-4 h-4 text-[#9095a1] transition-transform ${aperto ? "rotate-180" : ""}`} />
      </button>
      {aperto && (
        <div className="px-5 pb-5 flex flex-col gap-3">
          <div className="flex items-center gap-2.5 flex-wrap">
            <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#9095a1]">Non pagati nel mese di</label>
            <MonthPicker value={mese} onChange={setMese} className={`${FIELD_CLS} w-40 h-9`} data-testid="recovery-for-month" />
            <button type="button" onClick={carica} disabled={loading} className={SECONDARY_BTN}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </button>
          </div>
          {clienti.length === 0 ? (
            <EmptyState boxed>{loading ? "Caricamento…" : `Nessun cliente da recuperare per ${nomeMese(mese)}`}</EmptyState>
          ) : (
            <div className="flex flex-col gap-2">
              {clienti.map((c) => (
                <div key={c.client_id} className="flex items-center justify-between gap-3 p-3 border border-[#e9e9f1] rounded-[12px]" data-testid={`unpaid-${c.client_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[13.5px] text-[#15161c] truncate">{c.name}</div>
                    <div className="text-[12px] text-[#9095a1] scheda-font-mono">canone mensile: {euro(c.canone_mensile)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => recupera(c)}
                    disabled={saving === c.client_id}
                    className={SECONDARY_BTN}
                    data-testid={`recover-${c.client_id}`}
                  >
                    {saving === c.client_id ? "Registrazione…" : "Segna recuperato"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RecuperiPagamentiTab({ api, user }) {
  const canRegister = puoRegistrarePagamenti(user);
  const [dati, setDati] = useState(null);
  const [recuperi, setRecuperi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cerca, setCerca] = useState("");
  const meseCorrente = dati?.mese_corrente || new Date().toISOString().slice(0, 7);

  const caricaArretrati = async () => {
    setLoading(true);
    try {
      const r = await api.call("/compensation/arretrati");
      if (r.ok) setDati(await r.json());
    } finally {
      setLoading(false);
    }
  };
  const caricaRecuperi = async (mese = meseCorrente) => {
    const r = await api.call(`/compensation/payment-recoveries?recovered_in=${mese}`);
    if (r.ok) setRecuperi((await r.json()).recoveries || []);
  };

  useEffect(() => {
    caricaArretrati();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Il mese dei recuperi è quello del backend (fuso di Roma), non del browser.
  useEffect(() => {
    if (dati?.mese_corrente) caricaRecuperi(dati.mese_corrente);
  }, [dati?.mese_corrente]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRegistrato = (risposta, mese) => {
    avvisaRegistrato(risposta, mese);
    caricaArretrati();
    caricaRecuperi();
  };

  const rimuoviRecupero = async (r) => {
    const avviso = r.automatico
      ? `\n\nÈ stato creato dal pagamento registrato: il pagamento resta, si toglie solo l'accredito dei compensi.`
      : "";
    if (!window.confirm(`Rimuovere il recupero di "${r.client_name}"?${avviso}`)) return;
    const res = await api.call(`/compensation/payment-recoveries/${r.recovery_id}`, { method: "DELETE" });
    if (res.ok) {
      toast.success("Recupero rimosso");
      caricaRecuperi();
    } else toast.error("Errore nella rimozione");
  };

  const clienti = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    const tutti = dati?.clients || [];
    return q ? tutti.filter((c) => (c.name || "").toLowerCase().includes(q)) : tutti;
  }, [dati, cerca]);
  const totaleRecuperato = recuperi.reduce((s, r) => s + (r.total_delta || 0), 0);

  return (
    <div className="flex flex-col gap-5 scheda-font-ui" data-testid="recoveries-tab">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5 items-start">
        <SectionCard
          icon={AlertTriangle}
          iconBg="#fdecea"
          iconColor="#c0392b"
          title="Da recuperare"
          count={dati ? dati.count : null}
          testId="arretrati-card"
          action={
            <div className="flex items-center gap-4">
              <InfoHint
                title="Pagamenti arretrati"
                description="Mesi già finiti con canone, attivazione o addon non registrati nei Pagamenti. Il mese in corso non compare finché non è finito; un mese scoperto resta qui anche se i successivi sono pagati. Registrare un mese chiuso come non pagato accredita da solo i compensi di direzioni e commerciale nel payout di questo mese."
              />
              <StatBlock label="Totale" value={dati ? euro(dati.totale) : "—"} color={dati?.totale ? "#c0392b" : "#0f7040"} />
            </div>
          }
        >
          <div className="flex flex-col gap-3">
            {(dati?.count || 0) > 5 && (
              <div className="relative">
                <Search className="w-4 h-4 text-[#9095a1] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={cerca}
                  onChange={(e) => setCerca(e.target.value)}
                  placeholder="Cerca cliente"
                  className={`${FIELD_CLS} pl-9`}
                  data-testid="arretrati-cerca"
                />
              </div>
            )}
            {!dati ? (
              <div className="h-[66px] rounded-[14px] border border-[#e9e9f1] bg-[#fbfbfd] animate-pulse" />
            ) : clienti.length === 0 ? (
              <div className="flex items-center gap-2 px-4 py-3 rounded-[12px] bg-[#e7f7ee] text-[#0f7040] text-[13px] font-semibold">
                <CheckCircle2 className="w-4 h-4 shrink-0" strokeWidth={2.2} />
                {cerca ? "Nessun cliente trovato" : "Nessun pagamento arretrato: tutti i mesi passati sono registrati"}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {clienti.map((c, i) => (
                  <ClienteArretrato
                    key={c.client_id}
                    cliente={c}
                    canRegister={canRegister}
                    onRegistrato={onRegistrato}
                    apertoIniziale={clienti.length <= 3 && i === 0}
                  />
                ))}
              </div>
            )}
            {!canRegister && (dati?.count || 0) > 0 && (
              <p className="text-[12px] text-[#9095a1]">La registrazione dei pagamenti è riservata ad amministrazione e direzione commerciale.</p>
            )}
            {loading && dati && (
              <span className="text-[12px] text-[#9095a1] inline-flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Aggiornamento…
              </span>
            )}
          </div>
        </SectionCard>

        <SectionCard
          icon={Wallet}
          iconBg="#e7f7ee"
          iconColor="#0f7040"
          title={`Recuperati · ${maiuscola(nomeMese(meseCorrente))}`}
          count={recuperi.length}
          testId="recuperi-card"
          action={
            <InfoHint
              title="Recuperi del mese"
              description="Compensi di direzioni e commerciale trattenuti nei mesi in cui il cliente non aveva pagato, accreditati nel payout di questo mese perché il pagamento è arrivato dopo. Compaiono in Payout operativi."
            />
          }
        >
          {recuperi.length === 0 ? (
            <EmptyState boxed>Nessun recupero in {nomeMese(meseCorrente)}</EmptyState>
          ) : (
            <div className="flex flex-col gap-2">
              {recuperi.map((r) => (
                <div key={r.recovery_id} className="flex items-start gap-3 p-3 border border-[#d6f0e0] rounded-[12px] bg-[#f6fcf8]" data-testid={`recovery-${r.recovery_id}`}>
                  <CircleDollarSign className="w-4 h-4 text-[#0f7040] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[13.5px] font-semibold text-[#15161c]">{r.client_name}</span>
                      {r.automatico && <Badge tone="accent">Da pagamento</Badge>}
                    </div>
                    <div className="text-[12px] text-[#565d6d]">
                      Competenza {nomeMese(r.for_year_month)} · canone {euro(r.canone)}
                    </div>
                    <div className="text-[11.5px] text-[#9095a1] mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                      DT +{euro(r.dt_amount)} · DC +{euro(r.dc_amount)} · Sales +{euro(r.sales_amount)} ·
                      <UserAvatar name={r.recorded_by_name} picture={r.recorded_by_picture} size={16} />
                      {r.recorded_by_name}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="scheda-font-mono text-[#0f7040] font-bold text-[13.5px]">+{euro(r.total_delta)}</span>
                    <InfoTooltip title="Rimuovi recupero" description="Toglie l'accredito dal payout del mese. Un pagamento registrato resta registrato.">
                      <button type="button" onClick={() => rimuoviRecupero(r)} className={`${GHOST_BTN} !px-1.5`} aria-label="Rimuovi recupero">
                        <Trash2 className="w-3.5 h-3.5 text-[#c0392b]" />
                      </button>
                    </InfoTooltip>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-[#f0f0f6]">
                <span className="text-[12px] font-semibold text-[#565d6d] inline-flex items-center gap-1.5">
                  <Undo2 className="w-3.5 h-3.5" /> Totale recuperato
                </span>
                <span className="text-[15px] font-bold scheda-font-mono text-[#0f7040]">+{euro(totaleRecuperato)}</span>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      <SenzaRegistro api={api} meseCorrente={meseCorrente} onRecupero={() => caricaRecuperi()} />
    </div>
  );
}
